/**
 * Los cuatro actores de Smarty Drop: papeles, almacén, stock con dueño y lugar, remesas,
 * pedidos donde tengo parte y liquidación.
 *
 * Plan: docs/plan/PLAN-MAESTRO-ARQUITECTURA-DE-ACTORES-DROP-2026-09-24.md.
 *
 * ⭐ TODO PASA POR FUNCIONES DE LA BASE. Cada una verifica quién llama (`fn_drop_puede_actuar_por`)
 *    y devuelve exactamente lo que ese papel puede ver: el proveedor su stock y su kardex, el
 *    almacén lo que guarda y lo que le toca despachar. Ninguna pantalla lee las tablas directo.
 */
import { supabase } from '@/lib/supabase';
import {
  leerElEstadoDeCuenta, leerLoEmitido, type EstadoDeCuentaDelPeriodo, type EstadoDeCuentaEmitido,
} from '@/lib/dropshipping/elEstadoDeCuenta';
import type { EstadoDeCuenta } from '@/lib/dropshipping/estadosDeCuenta';

// ── Papeles ──────────────────────────────────────────────────────────────────────────────────
export type PapelDrop = 'revendedor' | 'proveedor' | 'almacen' | 'courier';

/** Los papeles se GANAN con un hecho: producto publicado, domicilio de almacén. No se marcan. */
export async function misPapeles(tenantId: string): Promise<PapelDrop[]> {
  const { data, error } = await supabase.rpc('fn_drop_mis_papeles', { p_tenant: tenantId });
  if (error) throw error;
  return ((data ?? []) as { papel: PapelDrop }[]).map((f) => f.papel);
}

// ── Almacén ──────────────────────────────────────────────────────────────────────────────────
export interface AlmacenDrop {
  tenant_id: string;
  nombre: string;
  direccion: string | null;
  departamento: string | null;
  provincia: string | null;
  distrito: string | null;
  hora_cierre_lima: string | null;
  hora_cierre_provincia: string | null;
  entrega_mismo_dia: boolean;
  activo: boolean;
  /** La agencia de Shalom de la que salen sus paquetes (20261023143000): el origen que impone a quien registra. */
  agencia_origen_id?: string | null;
  /** La sede de Olva de la que salen sus paquetes (20261030170000): Lima (43) salvo que diga otra. */
  olva_origen_hq?: string;
  /** Lo que cobra por empacar un contra entrega de Lima, del recargo del envío (20261027120000). La fija Smarty. */
  recargo_por_empacar?: number;
}

export async function miAlmacen(tenantId: string): Promise<AlmacenDrop | null> {
  const { data, error } = await supabase
    .from('drop_almacenes')
    .select('tenant_id, nombre, direccion, departamento, provincia, distrito, hora_cierre_lima, hora_cierre_provincia, entrega_mismo_dia, activo, agencia_origen_id, olva_origen_hq, recargo_por_empacar')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw error;
  return (data as AlmacenDrop | null) ?? null;
}

export async function guardarAlmacen(tenantId: string, datos: Partial<AlmacenDrop>): Promise<AlmacenDrop> {
  const { data, error } = await supabase.rpc('fn_drop_guardar_almacen', { p_tenant: tenantId, p_datos: datos });
  if (error) throw error;
  return data as AlmacenDrop;
}

/** Smarty HQ da de alta (o corrige) un almacén de la red, sin perfil de proveedor (20261023143000). */
export async function altaDeAlmacenDesdeHQ(tenantId: string, datos: Partial<AlmacenDrop>): Promise<AlmacenDrop> {
  const { data, error } = await supabase.rpc('fn_drop_alta_de_almacen', { p_tenant: tenantId, p_datos: datos });
  if (error) throw error;
  return data as AlmacenDrop;
}

/**
 * El proveedor dice de qué almacén sale su mercadería (20261023143000; como el almacén favorito de Aliclik): guarda el
 * preferido y muda todo su catálogo ahí. `null` = su propio local.
 */
export async function elegirAlmacen(tenantId: string, almacenId: string | null): Promise<{ almacen_id: string; productos_mudados: number }> {
  const { data, error } = await supabase.rpc('fn_drop_elegir_almacen', { p_tenant: tenantId, p_almacen: almacenId });
  if (error) throw error;
  return data as { almacen_id: string; productos_mudados: number };
}

// ── Stock con dueño y lugar ─────────────────────────────────────────────────────────────────
export interface StockEnAlmacen {
  /** La fila del catálogo de Drop. Null: un producto fuera del catálogo con stock en otro lugar (ADR-0049). */
  catalogo_id: string | null;
  product_id: string;
  producto: string;
  sku: string | null;
  /** La foto del producto, para la lista. Null si no tiene. */
  imagen: string | null;
  dueno_id: string;
  dueno: string;
  almacen_id: string;
  almacen: string;
  fisico: number;
  comprometido: number;
  disponible: number;
  /** Tope de lo que se ofrece en Drop. Null = todo lo disponible. */
  cupo: number | null;
  /** Lo que el catálogo ofrece desde este almacén: el disponible, con el cupo como tope. */
  en_drop: number;
  /** Lo que el dueño tiene en su bodega (de ahí sale una remesa). Null si la fila no es mía. */
  en_casa: number | null;
  punto_reposicion: number | null;
  bajo_reposicion: boolean;
  /** Quién lo ve en el catálogo: cualquiera, o sólo sus invitados. */
  visibilidad: Visibilidad;
  /**
   * La fila del lugar del catálogo. Un producto puede estar además en otros lugares (ADR-0049: North Express guarda
   * una parte de lo de Vitalex): esas filas dicen lo que hay ahí; el cupo y el aviso se fijan en la del catálogo.
   */
  del_catalogo: boolean;
}

export async function stockPorAlmacen(tenantId: string): Promise<StockEnAlmacen[]> {
  const { data, error } = await supabase.rpc('fn_drop_stock_por_almacen', { p_tenant: tenantId });
  if (error) throw error;
  return (data ?? []) as StockEnAlmacen[];
}

export interface MovimientoDeKardex {
  creado: string;
  /** El vocabulario de `inventory_movements`: initial · sale · return · restock · adjustment · transfer. */
  tipo: string;
  cantidad: number;
  almacen: string;
  pedido: string | null;
  motivo: string | null;
  saldo: number;
}

export async function kardex(tenantId: string, catalogoId: string): Promise<MovimientoDeKardex[]> {
  const { data, error } = await supabase.rpc('fn_drop_kardex', { p_tenant: tenantId, p_catalogo: catalogoId });
  if (error) throw error;
  return (data ?? []) as MovimientoDeKardex[];
}

/** El kardex de un producto que no está en el catálogo de Drop (el stock por lugar es del producto, ADR-0049). */
export async function kardexDelProducto(tenantId: string, productoId: string): Promise<MovimientoDeKardex[]> {
  const { data, error } = await supabase.rpc('fn_drop_kardex_del_producto', { p_tenant: tenantId, p_producto: productoId });
  if (error) throw error;
  return (data ?? []) as MovimientoDeKardex[];
}

/** Lo que dice cada tipo de movimiento, en palabras del almacén y del proveedor. */
export const TIPO_DE_MOVIMIENTO: Record<string, string> = {
  initial: 'Stock inicial',
  restock: 'Ingreso recibido',
  sale: 'Despachado',
  return: 'Devuelto',
  adjustment: 'Ajuste',
  transfer: 'Traslado',
};

export async function fijarCupo(tenantId: string, catalogoId: string, cupo: number | null): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_fijar_cupo', { p_tenant: tenantId, p_catalogo: catalogoId, p_cupo: cupo });
  if (error) throw error;
}

export async function fijarPuntoDeReposicion(tenantId: string, catalogoId: string, punto: number | null): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_fijar_punto_reposicion', {
    p_tenant: tenantId, p_catalogo: catalogoId, p_punto: punto,
  });
  if (error) throw error;
}

// ── Remesas: el proveedor declara, el almacén cuenta ───────────────────────────────────────────
export type EstadoDeRemesa = 'borrador' | 'enviada' | 'recibida' | 'cerrada' | 'anulada';
export type OrigenDeRemesa = 'bodega' | 'externo';

export const ORIGEN_DE_REMESA: Record<OrigenDeRemesa, string> = {
  bodega: 'Sale de tu bodega',
  externo: 'Llega directo (fábrica o compra)',
};

export interface LineaDeRemesa {
  linea_id: string;
  /** Null si el producto no está en el catálogo de Drop: la remesa va por producto (ADR-0049). */
  catalogo_id: string | null;
  producto_id: string;
  producto: string;
  sku: string | null;
  ean: string | null;
  declarado: number;
  recibido: number | null;
}

export interface RemesaDrop {
  id: string;
  codigo: string;
  /** Desde qué lado la estoy mirando. Un negocio puede ser las dos cosas en remesas distintas. */
  soy: 'dueno' | 'almacen';
  dueno: string;
  almacen: string;
  almacen_id: string;
  estado: EstadoDeRemesa;
  /** bodega: sale de la casa del dueño (traslado). externo: llega directo de fábrica o de una compra. */
  origen: OrigenDeRemesa;
  transporte: string | null;
  nota: string | null;
  nota_recepcion: string | null;
  created_at: string;
  enviada_at: string | null;
  recibida_at: string | null;
  lineas: LineaDeRemesa[];
  declarado: number;
  recibido: number | null;
  /** El almacén de destino lo opera Smarty (North Express): lo recibe Smarty con lo que ese almacén contó. */
  la_recibe_smarty: boolean;
}

/** Adónde se puede mandar mercadería (ADR-0049): mis almacenes, donde tengo stock y los couriers que la guardan. */
export interface DestinoDeRemesa {
  almacen_id: string;
  almacen: string;
  /** Mi propio almacén: un ingreso, no un traslado. */
  es_mio: boolean;
  /** Un courier que guarda mercadería y despacha desde ahí. */
  es_courier: boolean;
}

export async function destinosDeRemesa(tenantId: string): Promise<DestinoDeRemesa[]> {
  const { data, error } = await supabase.rpc('fn_drop_destinos_de_remesa', { p_tenant: tenantId });
  if (error) throw error;
  return (data ?? []) as DestinoDeRemesa[];
}

export const ESTADO_DE_REMESA: Record<EstadoDeRemesa, { etiqueta: string; tono: 'info' | 'attention' | 'success' | 'critical' | undefined }> = {
  borrador: { etiqueta: 'Borrador', tono: undefined },
  enviada: { etiqueta: 'En camino', tono: 'attention' },
  recibida: { etiqueta: 'Recibido', tono: 'info' },
  cerrada: { etiqueta: 'Cerrado', tono: 'success' },
  anulada: { etiqueta: 'Anulado', tono: 'critical' },
};

export async function misRemesas(tenantId: string): Promise<RemesaDrop[]> {
  const { data, error } = await supabase.rpc('fn_drop_mis_remesas', { p_tenant: tenantId });
  if (error) throw error;
  return (data ?? []) as RemesaDrop[];
}

/** Una línea de remesa al guardarla: el producto, y su fila del catálogo si la tiene. */
export interface LineaParaMandar {
  producto_id: string;
  catalogo_id?: string | null;
  declarado: number;
}

/** Lo que puedo mandar a un almacén: mis productos activos, publicados o no en Drop, con lo que tengo en casa. */
export interface ProductoParaRemesa {
  product_id: string;
  producto: string;
  sku: string | null;
  imagen: string | null;
  en_casa: number;
  catalogo_id: string | null;
}

export async function productosParaRemesa(tenantId: string): Promise<ProductoParaRemesa[]> {
  const { data, error } = await supabase.rpc('fn_drop_productos_para_remesa', { p_tenant: tenantId });
  if (error) throw error;
  return (data ?? []) as ProductoParaRemesa[];
}

export async function guardarRemesa(
  tenantId: string,
  datos: { remesaId?: string | null; almacenId: string; lineas: LineaParaMandar[]; transporte?: string; nota?: string; origen?: OrigenDeRemesa },
): Promise<string> {
  const { data, error } = await supabase.rpc('fn_drop_remesa_guardar', {
    p_tenant: tenantId,
    p_remesa: datos.remesaId ?? null,
    p_almacen: datos.almacenId,
    p_lineas: datos.lineas,
    p_transporte: datos.transporte ?? null,
    p_nota: datos.nota ?? null,
    p_origen: datos.origen ?? 'bodega',
  });
  if (error) throw error;
  return data as string;
}

/*
 * 🪤 UNA LLAMADA POR PASO, CON SU NOMBRE ESCRITO. Antes era `rpc(fn, …)` con el nombre en una variable:
 *    ninguna herramienta lo veía, y la auditoría del 26-sep dio estas tres funciones por muertas. Un
 *    nombre literal lo encuentra una búsqueda, lo cruza el candado de firmas y lo lee quien llega.
 */
export async function enviarRemesa(tenantId: string, remesaId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_remesa_enviar', { p_tenant: tenantId, p_remesa: remesaId });
  if (error) throw error;
}
export async function anularRemesa(tenantId: string, remesaId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_remesa_anular', { p_tenant: tenantId, p_remesa: remesaId });
  if (error) throw error;
}
export async function cerrarRemesa(tenantId: string, remesaId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_remesa_cerrar', { p_tenant: tenantId, p_remesa: remesaId });
  if (error) throw error;
}

/** Recibir ES el asiento: la base escribe el `restock` por lo contado, no por lo declarado. */
export async function recibirRemesa(
  tenantId: string, remesaId: string, conteo: { linea_id: string; recibido: number }[], nota?: string,
): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_remesa_recibir', {
    p_tenant: tenantId, p_remesa: remesaId, p_conteo: conteo, p_nota: nota ?? null,
  });
  if (error) throw error;
}

export interface ProductoPorEan {
  catalogo_id: string;
  product_id: string;
  producto: string;
  sku: string | null;
  dueno: string;
  almacen: string;
}

export async function buscarPorEan(tenantId: string, ean: string): Promise<ProductoPorEan[]> {
  const { data, error } = await supabase.rpc('fn_drop_buscar_por_ean', { p_tenant: tenantId, p_ean: ean });
  if (error) throw error;
  return (data ?? []) as ProductoPorEan[];
}

// ── Pedidos donde tengo parte ─────────────────────────────────────────────────────────────────
export interface PedidoConParte {
  order_id: string;
  numero: string;
  papel: 'vendedor' | 'proveedor' | 'almacen' | 'courier';
  creado: string;
  estado: string;
  pago: string;
  envio: string;
  vendedor: string | null;
  proveedor: string | null;
  almacen: string | null;
  /** Lo despacha un almacén que no es el proveedor: el proveedor lo ve sin datos del cliente. */
  consignado: boolean;
  unidades: number;
  lineas: { producto: string; cantidad: number }[];
}

export async function pedidosDondeTengoParte(tenantId: string, papel?: string): Promise<PedidoConParte[]> {
  const { data, error } = await supabase.rpc('fn_drop_pedidos_donde_tengo_parte', {
    p_tenant: tenantId, p_papel: papel ?? null,
  });
  if (error) throw error;
  return (data ?? []) as PedidoConParte[];
}

/** Lo que el almacén necesita para despachar un encargo consignado. Sólo se lo da la base al almacén. */
export interface EncargoParaDespachar {
  numero: string;
  /** Quién saca la guía en Shalom: el drop (por defecto) o el almacén. Lo elige el drop en su venta. */
  registra: 'revendedor' | 'almacen';
  /** El estado de envío del PEDIDO: dice «despachado» aunque el envío todavía no lo refleje. */
  estado_envio: string;
  destino: {
    nombre: string | null; razon_social: string | null; documento: string | null; tipo_documento: string | null;
    departamento: string | null; provincia: string | null; distrito: string | null;
    direccion: string | null; referencia: string | null;
  } | null;
  envio: {
    id: string; estado: string; courier: string | null; agencia: string | null; tamano: string | null;
    guia: string | null; clave: string | null; modo: string | null;
  } | null;
}

export async function encargoParaDespachar(tenantId: string, orderId: string): Promise<EncargoParaDespachar> {
  const { data, error } = await supabase.rpc('fn_drop_encargo_para_despachar', { p_tenant: tenantId, p_order: orderId });
  if (error) throw error;
  return data as EncargoParaDespachar;
}

// ── Liquidación: una fila por pedido (20260925172000) ────────────────────────────────────────
/**
 * Las cuentas de un negocio en el libro. Cada una tiene su saldo (las tarjetas de arriba); la
 * liquidación de un pedido suma, para ese pedido, las líneas de todas las cuentas del negocio.
 */
export type CuentaDeLiquidacion =
  | 'por_pagar' | 'almacen' | 'billetera' | 'por_recibir' | 'por_rendir' | 'courier'
  // El pago preparado, que ya salió del saldo (20260925215000). Lo muestra «Tus pagos».
  | 'en_camino';

export const NOMBRE_DE_LA_CUENTA: Record<CuentaDeLiquidacion, { titulo: string; corto: string; explica: string }> = {
  por_pagar: { titulo: 'Por liquidar', corto: 'Smarty te lo transfiere', explica: 'Tu producto vendido, menos fulfillment y comisión. Smarty te lo paga en la liquidación.' },
  almacen: { titulo: 'Tus despachos', corto: 'Smarty te lo transfiere', explica: 'Lo que ganaste empacando y despachando paquetes de otros proveedores.' },
  en_camino: { titulo: 'En camino a tu banco', corto: 'Ya salió de tu saldo', explica: 'El pago que Smarty preparó y está por transferirte.' },
  billetera: { titulo: 'Tu billetera', corto: 'Disponible', explica: 'Tu saldo en Smarty: recargas, lo que te rindió el courier y lo que pagaste con él.' },
  por_recibir: { titulo: 'Por recibir', corto: 'Lo tiene tu courier', explica: 'Lo que tu courier cobró y Smarty aún no confirmó. Cuando Smarty confirma la rendición, pasa a tu billetera.' },
  por_rendir: { titulo: 'Por rendir', corto: 'Lo depositas a Smarty', explica: 'Lo que cobraste contra entrega y tienes que entregarle a Smarty.' },
  courier: { titulo: 'Tus entregas', corto: 'Tus tarifas', explica: 'Lo que ganaste por cada entrega contra entrega.' },
};

/** Qué es cada sol de un pedido: el mismo nombre que su línea en el libro. */
export type ConceptoDeLiquidacion =
  | 'cobrado' | 'producto' | 'encargo' | 'fulfillment' | 'envio' | 'comision' | 'pos' | 'reembolso' | 'ajuste' | 'mercaderia'
  | 'recarga' | 'retiro' | 'rendicion';

export const NOMBRE_DEL_CONCEPTO: Record<ConceptoDeLiquidacion, string> = {
  cobrado: 'Cobrado al cliente',
  producto: 'Producto',
  encargo: 'Producto y despacho',
  fulfillment: 'Fulfillment',
  envio: 'Envío o despacho',
  comision: 'Comisión de la plataforma',
  pos: 'Comisión del POS',
  reembolso: 'Reembolso',
  ajuste: 'Descuentos adicionales',
  mercaderia: 'Mercadería del almacén',
  recarga: 'Pago por transferencia',
  retiro: 'Retiro',
  rendicion: 'Rendición del courier',
};

/** Cómo pagó el cliente al recibir un contra entrega (20260925181000). */
export type MedioDeCobro = 'efectivo' | 'transferencia' | 'pos';
export const MEDIO_DE_COBRO: Record<MedioDeCobro, string> = {
  efectivo: 'Efectivo', transferencia: 'Transferencia', pos: 'Tarjeta (POS)',
};

/** Dónde está la plata del pedido, como el «dinero a liberar» → «disponible» de Mercado Pago. */
/**
 * Dónde está la plata del pedido. `en_camino` y `liquidado` (20260925216000): el pago al banco que lo
 * cubre está preparado, o ya se transfirió — el pago guarda qué pedidos cubre (`drop_pagos_pedidos`).
 */
export type PlataDelPedido =
  | 'pendiente' | 'por_recibir' | 'disponible' | 'por_rendir' | 'rendido' | 'por_liquidar' | 'en_camino' | 'liquidado'
  | 'en_billetera' | 'sin_movimiento';
/**
 * En qué quedó la venta, con la regla de la liquidación (20260925215000). Por agencia se concreta al
 * salir («despachado»: lo que pase en la agencia escapa a la liquidación); por motorizado, al entregarse o
 * no («devuelto»). «Por despachar» y «en camino» son seguimiento: la plata está `pendiente`.
 */
export type EntregaDelPedido =
  | 'por_despachar' | 'en_camino' | 'despachado' | 'entregado'
  // Por motorizado, no se entregó: qué pasó en la puerta (20260925221000). `devuelto`, sin intento registrado.
  | 'rechazado' | 'no_contesta' | 'anulado_en_ruta' | 'devuelto'
  | 'anulado';

/** Si no se entregó, por lo que haya pasado: la pestaña «No entregados» y su cifra. */
export const NO_ENTREGADO: readonly EntregaDelPedido[] = ['rechazado', 'no_contesta', 'anulado_en_ruta', 'devuelto'];
export const esNoEntregado = (p: Pick<PedidoLiquidado, 'entrega'>): boolean => NO_ENTREGADO.includes(p.entrega);

/** Si la venta todavía no se concreta: no se liquida, es seguimiento (la pestaña «Pendientes»). */
export const estaPendiente = (p: Pick<PedidoLiquidado, 'plata'>): boolean => p.plata === 'pendiente';

/**
 * La plata de esta venta ya llegó (a la billetera o al banco): ya se liquidó. Founder (19-oct): «esos +67 y +67 ya los
 * pasó a su billetera… ¿qué acaso no tiene ese historial?» — «Monto a liquidar» los sumaba como si se debieran.
 */
export const YA_LIQUIDADO: readonly PlataDelPedido[] = ['disponible', 'en_billetera', 'liquidado', 'rendido'];
export type PapelEnElPedido = 'vendedor' | 'proveedor' | 'almacen' | 'courier' | 'otro';

/**
 * Un hecho del pedido y lo que le movió al negocio (positivo = a su favor). Una línea por hecho:
 * los repartos internos (lo del courier y lo de Smarty en un envío) no salen de la base.
 */
export interface HechoDelPedido {
  fecha: string;
  tipo: string;
  monto: number;
}

/** Un pedido con lo que le dejó (o le costó) al negocio, por concepto. Positivo = a su favor. */
export interface PedidoLiquidado {
  pedido_id: string;
  numero: string;
  /** El número del encargo al almacén, si la venta tiene uno. */
  encargo: string | null;
  /** Cuándo se movió la plata por última vez: el período de la liquidación. */
  fecha: string;
  fecha_pedido: string | null;
  cliente: string | null;
  /** Quién vendió (sólo la tienda vende), de quién es el producto y quién lo despacha. */
  tienda: string | null;
  proveedor: string | null;
  almacen: string | null;
  /** La mercadería es de quien la vendió: sin encargo, no se la compró a nadie (20260925199000). */
  mercaderia_propia: boolean;
  /** «1× Nitric Oxide Booster · 2× …», como la columna de Aliclik. */
  productos: string | null;
  papel: PapelEnElPedido;
  /** Cómo llegó: por motorizado (el courier cobra en la puerta) o por agencia. */
  modalidad: 'motorizado' | 'agencia';
  entrega: EntregaDelPedido;
  plata: PlataDelPedido;
  /** Cómo pagó el cliente al recibir, si fue por motorizado y ya se entregó. */
  medio: MedioDeCobro | null;
  cobrado: number;
  producto: number;
  fulfillment: number;
  envio: number;
  comision: number;
  /** Lo que se queda quien procesa la tarjeta si el cliente pagó con POS. Lo paga quien vende. */
  pos: number;
  reembolso: number;
  ajuste: number;
  /** Lo que el proveedor le paga al almacén por la mercadería despachada (marca blanca, 23-oct). */
  mercaderia: number;
  total: number;
  /** Lo que quien vende cobró por su cuenta (una venta de agencia): no pasa por Smarty. */
  cobrado_por_ti: number;
  historial: HechoDelPedido[];
}

const CIFRAS = ['cobrado', 'producto', 'fulfillment', 'envio', 'comision', 'pos', 'reembolso', 'ajuste', 'mercaderia', 'total', 'cobrado_por_ti'] as const;

/**
 * `pedidos`: sólo esos (los de una página de Ventas, 20260925178000). Es la columna Dinero de quien no
 * vendió: su parte sale de acá, la misma cifra que ve en Liquidación.
 */
/** Por dónde salió cada pedido del negocio (20261030150000): la agencia o el courier del envío, o null si no tiene. */
export async function porDondeSalen(tenantId: string, pedidos: string[]): Promise<Record<string, string | null>> {
  const { data, error } = await supabase.rpc('fn_drop_por_donde_salen', { p_tenant: tenantId, p_pedidos: pedidos });
  if (error) throw error;
  return Object.fromEntries(((data ?? []) as { pedido_id: string; transporte: string | null }[]).map((f) => [f.pedido_id, f.transporte]));
}

/**
 * La mercadería que volvió al estante o volvió a salir (20261102180000), una línea por asiento. Positivo = a favor del
 * negocio: lo que vuelve es negativo para el almacén, que ya la cobró, y positivo para el proveedor, que deja de deberla.
 */
export interface LineaDelEstante {
  pedido_id: string;
  asiento_id: string;
  fecha: string;
  monto: number;
  /** true: volvió al estante. false: volvió a salir. */
  vuelve: boolean;
}

export async function loQueVolvioAlEstante(tenantId: string): Promise<LineaDelEstante[]> {
  const { data, error } = await supabase.rpc('fn_drop_lo_que_volvio_al_estante', { p_tenant: tenantId });
  if (error) throw error;
  return ((data ?? []) as LineaDelEstante[]).map((l) => ({ ...l, monto: Number(l.monto) }));
}

export async function liquidacionPorPedido(
  tenantId: string, desde?: string, hasta?: string, pedidos?: string[],
): Promise<PedidoLiquidado[]> {
  const { data, error } = await supabase.rpc('fn_drop_liquidacion_por_pedido', {
    p_tenant: tenantId, p_desde: desde ?? null, p_hasta: hasta ?? null, p_pedidos: pedidos ?? null,
  });
  if (error) throw error;
  // Postgres manda los numeric como texto: se convierten una vez, acá.
  return ((data ?? []) as PedidoLiquidado[]).map((p) => {
    const cifras = Object.fromEntries(CIFRAS.map((k) => [k, Number(p[k])])) as Pick<PedidoLiquidado, (typeof CIFRAS)[number]>;
    const historial = (p.historial ?? []).map((h) => ({ ...h, monto: Number(h.monto) }));
    return { ...p, ...cifras, historial };
  });
}

/** Un tramo del por recibir: cuánto es y cuántas ventas son. */
export interface TramoDelPorRecibir { monto: number; ventas: number }
export interface PorRecibirDelNegocio {
  /** Lo que el courier cobró y todavía no depositó a Smarty. */
  recaudado: TramoDelPorRecibir;
  /** Lo que el courier ya depositó (rendición declarada) y Smarty todavía no confirmó. */
  en_revision: TramoDelPorRecibir;
}

/**
 * El «por recibir» partido en dónde está (20261026070000): Recaudado (lo tiene el courier) → En revisión (ya depositó;
 * Smarty confirma) → Tu billetera. Suma lo mismo que `misSaldos().por_recibir`.
 */
export async function miPorRecibir(tenantId: string): Promise<PorRecibirDelNegocio> {
  const { data, error } = await supabase.rpc('fn_drop_mi_por_recibir', { p_tenant: tenantId });
  if (error) throw error;
  const r: PorRecibirDelNegocio = { recaudado: { monto: 0, ventas: 0 }, en_revision: { monto: 0, ventas: 0 } };
  for (const fila of (data ?? []) as { estado: keyof PorRecibirDelNegocio; monto: number; ventas: number }[]) {
    if (fila.estado in r) r[fila.estado] = { monto: Number(fila.monto), ventas: Number(fila.ventas) };
  }
  return r;
}

/**
 * El estado de cuenta del negocio en un papel y un periodo (días de Lima, `AAAA-MM-DD`), 20261028009000: lo arma la base
 * desde el libro, lo cuadra contra él y deja ver sólo lo de ese actor; acá se vuelve a leer con los montos como números.
 * El dueño o el gerente del negocio, o Smarty.
 */
export async function estadoDeCuenta(tenantId: string, papel: EstadoDeCuenta, desde: string, hasta: string): Promise<EstadoDeCuentaDelPeriodo> {
  const { data, error } = await supabase.rpc('fn_drop_estado_de_cuenta', { p_tenant: tenantId, p_papel: papel, p_desde: desde, p_hasta: hasta });
  if (error) throw error;
  return leerElEstadoDeCuenta(data);
}

/**
 * Emitir el estado de cuenta (lo que se descarga): la base le da el siguiente número del negocio en ese papel y lo
 * archiva. El mismo documento ya emitido vuelve con su número de entonces (`reemitido`).
 */
export async function emitirEstadoDeCuenta(tenantId: string, papel: EstadoDeCuenta, desde: string, hasta: string): Promise<EstadoDeCuentaDelPeriodo> {
  const { data, error } = await supabase.rpc('fn_drop_emitir_estado_de_cuenta', { p_tenant: tenantId, p_papel: papel, p_desde: desde, p_hasta: hasta });
  if (error) throw error;
  return leerElEstadoDeCuenta(data);
}

/** Lo emitido del negocio en ese papel, del último al primero. */
export async function estadosDeCuentaEmitidos(tenantId: string, papel: EstadoDeCuenta): Promise<EstadoDeCuentaEmitido[]> {
  const { data, error } = await supabase.rpc('fn_drop_estados_de_cuenta_emitidos', { p_tenant: tenantId, p_papel: papel });
  if (error) throw error;
  return leerLoEmitido(data);
}

/** Uno del archivo, tal como se emitió: su PDF sale idéntico. */
export async function estadoDeCuentaEmitido(id: string): Promise<EstadoDeCuentaDelPeriodo> {
  const { data, error } = await supabase.rpc('fn_drop_estado_de_cuenta_emitido', { p_id: id });
  if (error) throw error;
  return leerElEstadoDeCuenta(data);
}

/** Un negocio y un papel suyo que movió plata: lo que Smarty puede pedir. */
export interface ActorConEstadoDeCuenta { tenant_id: string; nombre: string; papel: EstadoDeCuenta }

/** Smarty: cada actor con los papeles que movieron plata (la base sólo se lo da a la plataforma). */
export async function actoresParaEstadoDeCuenta(): Promise<ActorConEstadoDeCuenta[]> {
  const { data, error } = await supabase.rpc('fn_drop_actores_para_estado_de_cuenta');
  if (error) throw error;
  return (data ?? []) as ActorConEstadoDeCuenta[];
}

/** El saldo de cada cuenta del negocio (a su favor). Las que nunca se movieron no vienen. */
export async function misSaldos(tenantId: string): Promise<Partial<Record<CuentaDeLiquidacion, number>>> {
  const { data, error } = await supabase.rpc('fn_drop_mis_saldos', { p_tenant: tenantId });
  if (error) throw error;
  return Object.fromEntries(((data ?? []) as { cuenta: CuentaDeLiquidacion; saldo: number }[]).map((s) => [s.cuenta, Number(s.saldo)]));
}

/**
 * El resumen del período, como la pestaña de Aliclik: cuántos pedidos se concretaron, y cuánto por concepto.
 *
 * ⭐ SE RESUME LO CONCRETADO (20260925215000, founder: «la liquidación se da cuando la venta se concretó»).
 *    Lo pendiente —por despachar, o por motorizado en camino— no suma: se cuenta aparte (`pendientes`).
 *    Un anulado sin plata ni llega: la base no lo liquida.
 */
export interface ResumenDeLiquidacion {
  /** Los concretados: por agencia, despachados; por motorizado, entregados o no. */
  pedidos: number;
  /** Entregados por motorizado y despachados por agencia: la venta se hizo. */
  entregados: number;
  /** Entregados sobre concretados, en % (Aliclik: «Efectividad»). `null` sin pedidos. */
  efectividad: number | null;
  motorizado: number;
  agencia: number;
  /** Por motorizado, no se entregaron (rechazo, no contesta…): se cobra sólo el fulfillment. */
  noEntregados: number;
  /** De esos, los rechazados en puerta y los que no contestaron: cuestan distinto (20260925221000). */
  rechazados: number;
  noContesta: number;
  /** Los que todavía no se concretan: se liquidan en el próximo corte, cuando se concreten. */
  pendientes: number;
  cobrado: number;
  producto: number;
  fulfillment: number;
  envio: number;
  comision: number;
  pos: number;
  reembolso: number;
  ajuste: number;
  mercaderia: number;
  total: number;
  /** De `total`, lo que ya llegó a la billetera o al banco (`YA_LIQUIDADO`), y lo que todavía falta. */
  yaLiquidado: number;
  porLiquidar: number;
  /** Lo que quien vende cobró por su cuenta (ventas de agencia): no pasa por Smarty. */
  cobradoPorTi: number;
}

export function resumirLiquidacion(todos: PedidoLiquidado[]): ResumenDeLiquidacion {
  const pedidos = todos.filter((p) => !estaPendiente(p));
  const suma = (k: (typeof CIFRAS)[number]) => Math.round(pedidos.reduce((a, p) => a + p[k], 0) * 100) / 100;
  // 🪤 Las cifras suman filas; los CONTEOS, pedidos: el proveedor que despacha desde su almacén tiene dos
  //    filas del mismo pedido (20260925198000) y es un pedido, no dos.
  const unicos = [...new Map(pedidos.map((p) => [p.pedido_id, p])).values()];
  const entregados = unicos.filter((p) => p.entrega === 'entregado' || p.entrega === 'despachado').length;
  return {
    pedidos: unicos.length,
    entregados,
    efectividad: unicos.length > 0 ? Math.round((entregados / unicos.length) * 1000) / 10 : null,
    motorizado: unicos.filter((p) => p.modalidad === 'motorizado').length,
    agencia: unicos.filter((p) => p.modalidad === 'agencia').length,
    noEntregados: unicos.filter(esNoEntregado).length,
    rechazados: unicos.filter((p) => p.entrega === 'rechazado').length,
    noContesta: unicos.filter((p) => p.entrega === 'no_contesta').length,
    pendientes: new Set(todos.filter(estaPendiente).map((p) => p.pedido_id)).size,
    cobrado: suma('cobrado'),
    producto: suma('producto'),
    fulfillment: suma('fulfillment'),
    envio: suma('envio'),
    comision: suma('comision'),
    pos: suma('pos'),
    reembolso: suma('reembolso'),
    ajuste: suma('ajuste'),
    mercaderia: suma('mercaderia'),
    total: suma('total'),
    yaLiquidado: Math.round(pedidos.filter((p) => YA_LIQUIDADO.includes(p.plata)).reduce((a, p) => a + p.total, 0) * 100) / 100,
    porLiquidar: Math.round(pedidos.filter((p) => !YA_LIQUIDADO.includes(p.plata)).reduce((a, p) => a + p.total, 0) * 100) / 100,
    cobradoPorTi: suma('cobrado_por_ti'),
  };
}

// ── Las tarifas de Smarty (20260925172000) ────────────────────────────────────────────────────
/** Lo que Smarty cobra en cada pedido. Una sola fila en la base; la cambia sólo la plataforma. */
export interface TarifasDeSmarty {
  /** El despacho a la agencia (Shalom, Olva): lo que paga quien vende por pedido. */
  despacho_a_agencia: number;
  /**
   * De eso, lo que gana el almacén que lleva el paquete; el resto es de Smarty (20261012110000).
   * `null` para quien no es almacén ni la plataforma, igual que `fulfillment_para_el_almacen`.
   */
  despacho_para_el_almacen: number | null;
  /** Lo que paga el proveedor por cada paquete que prepara un almacén ajeno. */
  fulfillment_por_paquete: number;
  /** De eso, lo que es del almacén. El resto es de Smarty, y no lo ve nadie más que la plataforma. */
  /** Lo que gana el almacén por paquete. `null` para quien no es almacén ni la plataforma (es interno). */
  fulfillment_para_el_almacen: number | null;
  /** Lo que se suma a la tarifa del courier en cada envío por motorizado. */
  recargo_envio_contra_entrega: number;
  /** Comisión por uso de la plataforma sobre lo entregado, en %. */
  comision_pct: number;
  /** Si un pedido no se entrega, quién paga el fulfillment que se le cobró al proveedor. */
  fulfillment_fallido_lo_paga: 'vendedor' | 'proveedor';
  /** Si está prendido, quien no completó su perfil no opera (20260925177000). */
  exigir_perfil_completo: boolean;
  /** Debajo de esto no se transfiere: se acumula para el pago siguiente (20260925179000). */
  pago_minimo: number;
  /** Lo que se queda quien procesa la tarjeta, en %; lo paga quien vende (20260925181000). */
  comision_pos_pct: number;
}

/**
 * Las tarifas que ESTE negocio necesita para operar (20260925201000). La parte del almacén en el
 * fulfillment llega sólo a la plataforma y a quien es almacén (es lo que gana); a los demás, `null`.
 * La tabla la lee sólo la plataforma: «lo interno no se muestra».
 */
export async function tarifasDeSmarty(tenantId: string): Promise<TarifasDeSmarty> {
  const { data, error } = await supabase.rpc('fn_drop_tarifas_vigentes', { p_tenant: tenantId });
  if (error) throw error;
  const d = data as Record<string, unknown>;
  return {
    despacho_a_agencia: Number(d.despacho_a_agencia),
    despacho_para_el_almacen: d.despacho_para_el_almacen == null ? null : Number(d.despacho_para_el_almacen),
    fulfillment_por_paquete: Number(d.fulfillment_por_paquete),
    fulfillment_para_el_almacen: d.fulfillment_para_el_almacen == null ? null : Number(d.fulfillment_para_el_almacen),
    recargo_envio_contra_entrega: Number(d.recargo_envio_contra_entrega),
    comision_pct: Number(d.comision_pct),
    fulfillment_fallido_lo_paga: d.fulfillment_fallido_lo_paga as TarifasDeSmarty['fulfillment_fallido_lo_paga'],
    exigir_perfil_completo: d.exigir_perfil_completo !== false,
    pago_minimo: Number(d.pago_minimo ?? 0),
    comision_pos_pct: Number(d.comision_pos_pct ?? 0),
  };
}

export async function fijarTarifasDeSmarty(t: TarifasDeSmarty): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_fijar_tarifas', {
    p_despacho: t.despacho_a_agencia, p_fulfillment: t.fulfillment_por_paquete,
    p_para_el_almacen: t.fulfillment_para_el_almacen, p_recargo: t.recargo_envio_contra_entrega,
    p_comision_pct: t.comision_pct, p_fallido: t.fulfillment_fallido_lo_paga,
    p_exigir_perfil: t.exigir_perfil_completo, p_pago_minimo: t.pago_minimo, p_comision_pos: t.comision_pos_pct,
    p_despacho_para_el_almacen: t.despacho_para_el_almacen,
  });
  if (error) throw error;
}

// ── Quién ve cada producto (privado por producto, como Dropi) ─────────────────────────────────
/** Un revendedor al que el proveedor puede invitar: ya le compró o importó algo suyo. */
export interface RevendedorMio {
  revendedor_id: string;
  nombre: string;
  encargos: number;
  importados: number;
}

export async function misRevendedores(tenantId: string): Promise<RevendedorMio[]> {
  const { data, error } = await supabase.rpc('fn_drop_mis_revendedores', { p_tenant: tenantId });
  if (error) throw error;
  return (data ?? []) as RevendedorMio[];
}

export type Visibilidad = 'publico' | 'privado';

export async function fijarVisibilidad(tenantId: string, catalogoId: string, visibilidad: Visibilidad): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_fijar_visibilidad', { p_tenant: tenantId, p_catalogo: catalogoId, p_visibilidad: visibilidad });
  if (error) throw error;
}

export interface InvitadoDelProducto {
  revendedor_id: string;
  nombre: string;
  desde: string;
  /** Null = sin vencimiento. Una exclusividad lleva fecha. */
  hasta: string | null;
  motivo: string | null;
}

export async function invitadosDelProducto(tenantId: string, catalogoId: string): Promise<InvitadoDelProducto[]> {
  const { data, error } = await supabase.rpc('fn_drop_invitados_del_producto', { p_tenant: tenantId, p_catalogo: catalogoId });
  if (error) throw error;
  return (data ?? []) as InvitadoDelProducto[];
}

export async function invitarAlProducto(
  tenantId: string, catalogoId: string, revendedorId: string, hasta: string | null, motivo?: string,
): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_invitar_al_producto', {
    p_tenant: tenantId, p_catalogo: catalogoId, p_revendedor: revendedorId, p_hasta: hasta, p_motivo: motivo ?? null,
  });
  if (error) throw error;
}

export async function quitarDelProducto(tenantId: string, catalogoId: string, revendedorId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_quitar_del_producto', { p_tenant: tenantId, p_catalogo: catalogoId, p_revendedor: revendedorId });
  if (error) throw error;
}
